import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import {
  aspectsForRotation,
  aspectValue,
  bakeStoryCrop,
  flipAspect,
  rotateSource,
  type AspectId,
} from './storyCrop'

export type StoryTextAlign = 'left' | 'center' | 'right'

export interface StoryText {
  value: string
  align: StoryTextAlign
  color: string
}

const COLORS = ['#ffffff', '#000000', '#00aff0', '#ff2e63', '#ffd166', '#06d6a0'] as const

/** Bake centered/aligned caption onto a photo (OF story text is client-composited). */
export async function bakeStoryCaption(file: File, text: StoryText | null): Promise<File> {
  if (!text?.value.trim() || !file.type.startsWith('image/')) return file
  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(img, 0, 0)
    const size = Math.max(24, Math.round(canvas.width * 0.045))
    // A canvas can't read CSS variables, so the brand UI font is named here.
    ctx.font = `700 ${size}px Poppins, Montserrat, system-ui, sans-serif`
    ctx.fillStyle = text.color
    ctx.textBaseline = 'middle'
    ctx.textAlign = text.align === 'left' ? 'left' : text.align === 'right' ? 'right' : 'center'
    const x = text.align === 'left' ? canvas.width * 0.08 : text.align === 'right' ? canvas.width * 0.92 : canvas.width / 2
    const y = canvas.height * 0.5
    const lines = wrapLines(ctx, text.value.trim(), canvas.width * 0.84)
    const lineH = size * 1.25
    const startY = y - ((lines.length - 1) * lineH) / 2
    ctx.shadowColor = 'rgba(0,0,0,0.55)'
    ctx.shadowBlur = Math.round(size * 0.35)
    lines.forEach((line, i) => ctx.fillText(line, x, startY + i * lineH))
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))
    if (!blob) return file
    return new File([blob], file.name.replace(/\.\w+$/, '') + '-story.jpg', { type: 'image/jpeg' })
  } finally {
    URL.revokeObjectURL(url)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to read image'))
    img.src = src
  })
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/)
  const lines: string[] = []
  let cur = ''
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w
    if (ctx.measureText(next).width > maxWidth && cur) {
      lines.push(cur)
      cur = w
    } else cur = next
  }
  if (cur) lines.push(cur)
  return lines.length ? lines : [text]
}

type Mode = 'stage' | 'text' | 'crop'

function AspectGlyph({ id }: { id: AspectId }) {
  const r = aspectValue(id)
  const h = 14
  const w = Math.max(6, Math.min(22, Math.round(h * r)))
  return <span className="ofh-story-ed-aspect-glyph" style={{ width: w, height: h }} aria-hidden="true" />
}

/** Live OF crop preview: blur cover + sharp contain at aspect/rotation. */
function CropPreview({ src, aspect, rotation }: { src: string; aspect: AspectId; rotation: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const ratio = aspectValue(aspect)

  useEffect(() => {
    let alive = true
    const canvas = canvasRef.current
    if (!canvas || !src) return
    const img = new Image()
    img.onload = () => {
      if (!alive) return
      const srcCanvas = rotateSource(img, rotation)
      const maxCss = 720
      const outW = ratio >= 1 ? maxCss : Math.max(1, Math.round(maxCss * ratio))
      const outH = ratio >= 1 ? Math.max(1, Math.round(maxCss / ratio)) : maxCss
      canvas.width = outW
      canvas.height = outH
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const sw = srcCanvas.width
      const sh = srcCanvas.height
      const cover = Math.max(outW / sw, outH / sh)
      ctx.filter = 'blur(28px)'
      ctx.drawImage(srcCanvas, (outW - sw * cover) / 2, (outH - sh * cover) / 2, sw * cover, sh * cover)
      ctx.filter = 'none'
      ctx.fillStyle = 'rgba(0,0,0,0.18)'
      ctx.fillRect(0, 0, outW, outH)
      const fit = Math.min(outW / sw, outH / sh)
      ctx.drawImage(srcCanvas, (outW - sw * fit) / 2, (outH - sh * fit) / 2, sw * fit, sh * fit)
    }
    img.src = src
    return () => {
      alive = false
    }
  }, [src, aspect, rotation, ratio])

  return (
    <canvas
      ref={canvasRef}
      className="ofh-story-ed-crop-frame"
      style={{ aspectRatio: `${ratio}` }}
      aria-hidden="true"
    />
  )
}

/**
 * OnlyFans story compose shell (live 2026-10-14): black stage, top tools,
 * centered media, white "SAVE TO YOUR STORY". Crop mode matches OF: aspect
 * chips, rotate, blur letterbox frame, Done.
 */
export function StoryEditor({
  file,
  onClose,
  onSave,
}: {
  file: File
  onClose: () => void
  onSave: (file: File) => Promise<void>
}) {
  const { t } = useTranslation('ofHome')
  const [working, setWorking] = useState(file)
  const isVideo = working.type.startsWith('video/')
  const [preview, setPreview] = useState('')
  const [mode, setMode] = useState<Mode>('stage')
  const [more, setMore] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [draftText, setDraftText] = useState('')
  const [align, setAlign] = useState<StoryTextAlign>('center')
  const [color, setColor] = useState<string>(COLORS[0])
  const [committed, setCommitted] = useState<StoryText | null>(null)
  const [history, setHistory] = useState<(StoryText | null)[]>([null])
  const [histIdx, setHistIdx] = useState(0)
  const [fileHist, setFileHist] = useState<File[]>([file])
  const [fileIdx, setFileIdx] = useState(0)
  const [aspect, setAspect] = useState<AspectId>('9:16')
  const [rotation, setRotation] = useState(0)
  const textRef = useRef<HTMLTextAreaElement>(null)
  const previewRef = useRef('')

  useEffect(() => {
    let alive = true
    setError('')
    setPreview('')
    if (working.type.startsWith('image/') || !working.type) {
      const reader = new FileReader()
      reader.onload = () => {
        if (!alive) return
        const url = String(reader.result || '')
        previewRef.current = url
        setPreview(url)
      }
      reader.onerror = () => {
        if (alive) setError(t('stories.previewFailed'))
      }
      reader.readAsDataURL(working)
      return () => {
        alive = false
      }
    }
    const url = URL.createObjectURL(working)
    previewRef.current = url
    setPreview(url)
    return () => {
      alive = false
      URL.revokeObjectURL(url)
      if (previewRef.current === url) previewRef.current = ''
    }
  }, [working, t])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (mode === 'text' || mode === 'crop') setMode('stage')
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, onClose])

  useEffect(() => {
    if (mode === 'text') textRef.current?.focus()
  }, [mode])

  const pushText = (next: StoryText | null) => {
    setHistory((h) => [...h.slice(0, histIdx + 1), next])
    setHistIdx((i) => i + 1)
    setCommitted(next)
  }
  const pushFile = (next: File) => {
    const stack = [...fileHist.slice(0, fileIdx + 1), next]
    setFileHist(stack)
    setFileIdx(stack.length - 1)
    setWorking(next)
  }

  const canUndo = histIdx > 0 || fileIdx > 0
  const canRedo = histIdx < history.length - 1 || fileIdx < fileHist.length - 1

  const undo = () => {
    if (fileIdx > 0) {
      const i = fileIdx - 1
      setFileIdx(i)
      setWorking(fileHist[i]!)
      return
    }
    if (histIdx <= 0) return
    const i = histIdx - 1
    setHistIdx(i)
    setCommitted(history[i] ?? null)
  }
  const redo = () => {
    if (fileIdx < fileHist.length - 1) {
      const i = fileIdx + 1
      setFileIdx(i)
      setWorking(fileHist[i]!)
      return
    }
    if (histIdx >= history.length - 1) return
    const i = histIdx + 1
    setHistIdx(i)
    setCommitted(history[i] ?? null)
  }

  const openCrop = () => {
    if (isVideo) {
      setError(t('stories.cropPhotoOnly'))
      return
    }
    setError('')
    setAspect('9:16')
    setRotation(0)
    setMode('crop')
  }

  const rotateCrop = () => {
    const next = (rotation + 90) % 360
    setRotation(next)
    setAspect(flipAspect(aspect))
  }

  const resetPortrait = () => {
    if (rotation === 0 || rotation === 180) return
    setRotation(0)
    setAspect(flipAspect(aspect))
  }

  const doneCrop = async () => {
    setBusy(true)
    setError('')
    try {
      const next = await bakeStoryCrop(working, aspect, rotation)
      pushFile(next)
      setMode('stage')
      setRotation(0)
      setAspect('9:16')
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }

  const doneText = () => {
    const value = draftText.trim()
    pushText(value ? { value, align, color } : null)
    setMode('stage')
    setDraftText('')
  }

  const download = () => {
    setMore(false)
    const a = document.createElement('a')
    a.href = preview
    a.download = working.name || 'story'
    a.click()
  }

  const save = async () => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const out = await bakeStoryCaption(working, committed)
      await onSave(out)
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('error'))
      setBusy(false)
    }
  }

  const overlay = mode === 'text' ? { value: draftText, align, color } : committed
  const cropAspects = aspectsForRotation(rotation)
  const inPortrait = rotation === 0 || rotation === 180

  const ui = (
    <div className="ofh-story-ed" role="dialog" aria-modal="true" aria-label={t('stories.editorTitle')}>
      {mode === 'stage' ? (
        <header className="ofh-story-ed-bar">
          <div className="ofh-story-ed-group">
            <button type="button" className="ofh-story-ed-icon" aria-label={t('close')} onClick={onClose}>
              <FeatherIcon name="x" size={22} />
            </button>
            <button type="button" className="ofh-story-ed-icon" aria-label={t('stories.undo')} disabled={!canUndo} onClick={undo}>
              <FeatherIcon name="rotate-ccw" size={20} />
            </button>
            <button type="button" className="ofh-story-ed-icon" aria-label={t('stories.redo')} disabled={!canRedo} onClick={redo}>
              <FeatherIcon name="rotate-cw" size={20} />
            </button>
          </div>
          <div className="ofh-story-ed-group">
            <button
              type="button"
              className="ofh-story-ed-icon"
              aria-label={t('stories.stickers')}
              title={t('stories.stickersSoon')}
              onClick={() => setError(t('stories.stickersSoon'))}
            >
              <FeatherIcon name="smile" size={20} />
            </button>
            <button
              type="button"
              className="ofh-story-ed-icon"
              aria-label={t('stories.crop')}
              disabled={isVideo}
              title={isVideo ? t('stories.cropPhotoOnly') : undefined}
              onClick={openCrop}
            >
              <FeatherIcon name="crop" size={20} />
            </button>
            <button
              type="button"
              className="ofh-story-ed-icon"
              aria-label={t('stories.text')}
              disabled={isVideo}
              title={isVideo ? t('stories.textPhotoOnly') : undefined}
              onClick={() => {
                setDraftText(committed?.value ?? '')
                setAlign(committed?.align ?? 'center')
                setColor(committed?.color ?? COLORS[0])
                setMode('text')
                setError('')
              }}
            >
              <span className="ofh-story-ed-aa" aria-hidden="true">
                Aa
              </span>
            </button>
            <div className="ofh-story-ed-more-wrap">
              <button type="button" className="ofh-story-ed-icon" aria-label={t('stories.more')} aria-expanded={more} onClick={() => setMore((v) => !v)}>
                <FeatherIcon name="more-vertical" size={20} />
              </button>
              {more && (
                <>
                  <div className="ofh-story-ed-scrim" onClick={() => setMore(false)} />
                  <div className="ofh-story-ed-menu" role="menu">
                    <button type="button" role="menuitem" className="ofh-story-ed-menu-item" onClick={download}>
                      {t('stories.download')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="ofh-story-ed-menu-item"
                      onClick={() => {
                        setMore(false)
                        setError(t('stories.tagsSoon'))
                      }}
                    >
                      {t('stories.tags')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
      ) : mode === 'crop' ? (
        <header className="ofh-story-ed-bar">
          <div className="ofh-story-ed-group">
            <button
              type="button"
              className="ofh-story-ed-icon"
              aria-label={t('stories.cropPortrait')}
              disabled={inPortrait}
              onClick={resetPortrait}
            >
              <FeatherIcon name="crop" size={20} />
            </button>
            <button type="button" className="ofh-story-ed-icon" aria-label={t('stories.rotate')} onClick={rotateCrop}>
              <FeatherIcon name="rotate-cw" size={20} />
            </button>
          </div>
          <button type="button" className="ofh-story-ed-done" disabled={busy} onClick={() => void doneCrop()}>
            {t('stories.done')}
          </button>
        </header>
      ) : (
        <header className="ofh-story-ed-bar ofh-story-ed-bar--text">
          <div className="ofh-story-ed-group">
            <button type="button" className="ofh-story-ed-icon" aria-label={t('stories.text')} onClick={() => textRef.current?.focus()}>
              <span className="ofh-story-ed-aa" aria-hidden="true">
                Aa
              </span>
            </button>
            <span className="ofh-story-ed-colors" role="group" aria-label={t('stories.color')}>
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`ofh-story-ed-swatch ${color === c ? 'is-on' : ''}`}
                  style={{ background: c }}
                  aria-label={c}
                  onClick={() => setColor(c)}
                />
              ))}
            </span>
            <button
              type="button"
              className="ofh-story-ed-icon"
              aria-label={t('stories.align')}
              onClick={() => setAlign((a) => (a === 'center' ? 'left' : a === 'left' ? 'right' : 'center'))}
            >
              <FeatherIcon name="align-center" size={20} />
            </button>
          </div>
          <button type="button" className="ofh-story-ed-done" onClick={doneText}>
            {t('stories.done')}
          </button>
        </header>
      )}

      <div className={`ofh-story-ed-stage ${mode === 'crop' ? 'is-crop' : ''}`}>
        {mode === 'crop' && preview ? (
          <CropPreview src={preview} aspect={aspect} rotation={rotation} />
        ) : preview ? (
          isVideo ? (
            <video className="ofh-story-ed-media" src={preview} controls playsInline preload="metadata" />
          ) : (
            <img
              className="ofh-story-ed-media"
              src={preview}
              alt=""
              draggable={false}
              onError={(e) => {
                if (e.currentTarget.src === previewRef.current) setError(t('stories.previewFailed'))
              }}
            />
          )
        ) : (
          <div className="ofh-story-ed-loading" aria-hidden="true" />
        )}

        {mode !== 'crop' && overlay?.value ? (
          <div className={`ofh-story-ed-caption is-${overlay.align}`} style={{ color: overlay.color }}>
            {overlay.value}
          </div>
        ) : null}
        {mode === 'text' && (
          <textarea
            ref={textRef}
            className={`ofh-story-ed-input is-${align}`}
            style={{ color }}
            value={draftText}
            placeholder={t('stories.textPlaceholder')}
            aria-label={t('stories.text')}
            rows={3}
            onChange={(e) => setDraftText(e.target.value)}
          />
        )}
      </div>

      {mode === 'crop' && (
        <div className="ofh-story-ed-aspects" role="group" aria-label={t('stories.aspect')}>
          {cropAspects.map((id) => (
            <button
              key={id}
              type="button"
              className={`ofh-story-ed-aspect ${aspect === id ? 'is-on' : ''}`}
              disabled={aspect === id}
              onClick={() => setAspect(id)}
            >
              <AspectGlyph id={id} />
              <span>{id}</span>
            </button>
          ))}
        </div>
      )}

      {error && (
        <p className="ofh-story-ed-error" role="alert">
          {error}
        </p>
      )}

      <footer className="ofh-story-ed-foot">
        <button type="button" className={`ofh-story-ed-save${busy ? ' om-busy' : ''}`} disabled={busy || mode !== 'stage'} onClick={() => void save()}>
          {t('stories.save')}
        </button>
      </footer>
    </div>
  )

  return createPortal(ui, document.body)
}
